import React, { useEffect, useState } from "react";

import { Container, Header, LogsContainer, LogItem, Circle, Line, LogsList } from "./styles";

import {
  CancelButton,
  SearchBar,
  SearchButton,
  SearchContainer,
  ClearSearch,
  TableFooter,
} from "@components/DataTable/styles";

//Icons
import { FiSearch } from "react-icons/fi";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";

//Custom Components
import ProfileButton from "@components/ProfileButton/ProfileButton";
import PageLoading from "@components/PageLoading/PageLoading";

//MUI
import Menu from "@mui/material/Menu";
import { searchHelper } from "@helpers/search";
import { filtersHelper } from "@helpers/filters";
import LogsFilter from "@components/Filters/LogsFilter/LogsFilter";
import { GetSystemLogsUseCase } from "@useCases/index";
import { HttpLogRepository } from "@data/repositories/Http/HttpLogRepository";
import { api } from "@data/api";
import { Log } from "@domain/models/Log";
import { logsHelper } from "@helpers/logs";

const perPage = 40;

const SystemLogsPage = () => {
  const [loading, setLoading] = useState(true);

  const [systemLogs, setSystemLogs] = useState<Log[]>([]);

  const [filters, setFilters] = useState([]);

  const [rangeOfList, setRangeOfList] = useState({ init: 0, finish: perPage });

  const [search, setSearch] = useState("");
  const [draftSearch, setDraftSearch] = useState("");
  const [searchBar, setSearchBar] = useState(null);
  const openSearch = Boolean(searchBar);

  useEffect(() => {
    (async () => {
      const useCase = new GetSystemLogsUseCase(new HttpLogRepository(api));

      const logs = await useCase.execute();

      setSystemLogs(logs);
      setLoading(false);
    })();
  }, []);

  const clearSearch = () => {
    setSearch("");
    setDraftSearch("");
  };

  const handleSearch = () => {
    setSearchBar(null);
    setSearch(draftSearch);
  };

  useEffect(() => {
    if (!draftSearch) {
      clearSearch();
    }
  }, [draftSearch]);

  const processLogs = (logDocs: Log[]) => {
    return logsHelper.mapAllUsersLogs(logDocs);
  };

  if (loading) {
    return (
      <Container>
        <PageLoading />
      </Container>
    );
  }

  const searchLogs = (backupTableData) => {
    const arrayLogs = [];

    for (const log of backupTableData) {
      if (searchHelper.searchText(log, search)) {
        arrayLogs.push(log);
      }
    }

    return arrayLogs;
  };

  const filterByTable = (data: Log[]) => {
    const filteredDocs = filtersHelper.filterDataTable(data, filters);

    return processLogs(filteredDocs);
  };

  const clearFilters = () => {
    setFilters([]);
  };

  const baseTableData = filters.length > 0 ? filterByTable(systemLogs) : processLogs(systemLogs);

  const backupTableData = processLogs(systemLogs);

  const tableData = search ? searchLogs(filterByTable(systemLogs)) : baseTableData;

  return (
    <Container>
      <Header>
        <div>
          <h1>Log do Sistema</h1>
          <FiSearch
            onClick={(event) => {
              setSearchBar(event.currentTarget);
            }}
            size={20}
            cursor="pointer"
            color="var(--grey2)"
          />
          <ClearSearch>
            {draftSearch && (
              <>
                {draftSearch}
                <AiOutlineCloseCircle cursor="pointer" onClick={() => clearSearch()} />
              </>
            )}
          </ClearSearch>
        </div>

        <div>
          <LogsFilter
            defaultTitle=""
            filtersHighlights={[]}
            filters={filters}
            filterTypes={[
              { headerName: "Ação", field: "log" },
              { headerName: "Data", field: "time" },
              { headerName: "Operador", field: "logOperator" },
            ]}
            customFilterOptions={[
              { label: "Entrou no sistema", value: "acessou" },
              { label: "Visualizou as informações", value: "visualizou" },
              { label: "Atendimentos", value: "atendeu" },
              { label: "Criou uma rota", value: "criou" },
              { label: "Erros", value: "erro" },
            ]}
            tableRowsBackup={backupTableData}
            onChangeFilters={(newFiltersArray) => setFilters(newFiltersArray)}
            onClearFilters={() => clearFilters()}
          />
          <ProfileButton arrowColor="var(grey2)" />
        </div>
      </Header>

      <LogsContainer>
        <LogsList>
          {tableData &&
            tableData.slice(rangeOfList.init, rangeOfList.finish).map((item, index) => (
              <LogItem key={index}>
                <div>
                  <Circle />
                  <Line />
                </div>
                <h3>{item}</h3>
              </LogItem>
            ))}
        </LogsList>
        <TableFooter>
          <h3>
            {rangeOfList.init} - {rangeOfList.finish}
          </h3>
          <div>
            <MdKeyboardArrowLeft
              size={20}
              cursor="pointer"
              onClick={() => {
                if (rangeOfList.finish == systemLogs.length) {
                  const value = rangeOfList.finish - rangeOfList.init + perPage;

                  const newFinish = rangeOfList.init;

                  setRangeOfList({ init: rangeOfList.finish - value, finish: newFinish });
                } else {
                  if (rangeOfList.init - perPage >= 0) {
                    setRangeOfList({
                      init: rangeOfList.init - perPage,
                      finish: rangeOfList.finish - perPage,
                    });
                  }
                }
              }}
            />
            <MdKeyboardArrowRight
              size={20}
              cursor="pointer"
              onClick={() => {
                if (systemLogs.length > perPage) {
                  if (rangeOfList.finish + perPage <= systemLogs.length) {
                    setRangeOfList({
                      init: rangeOfList.finish,
                      finish: rangeOfList.finish + perPage,
                    });
                  } else {
                    if (rangeOfList.finish != systemLogs.length) {
                      const value = rangeOfList.finish + (systemLogs.length - rangeOfList.finish);

                      setRangeOfList({ init: rangeOfList.finish, finish: value });
                    }
                  }
                }
              }}
            />
          </div>
          <h3>{systemLogs ? systemLogs.length : 0} clientes</h3>
        </TableFooter>
      </LogsContainer>

      <Menu
        id="basic-menu"
        anchorEl={searchBar}
        open={openSearch}
        onClose={() => {
          setSearchBar(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          style: {
            width: 600,
            height: 40,
            maxHeight: 40,
            borderRadius: 20,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            overflow: "hidden",
          },
        }}
      >
        <SearchContainer
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <FiSearch size={18} cursor="pointer" color="#626F87" strokeWidth="3" />
          <p>&#8288;</p>
          <SearchBar>
            <input
              name="draftSearch"
              onChange={(event) => setDraftSearch(event.target.value)}
              value={draftSearch}
              placeholder="Buscar..."
              autoFocus={true}
            />
          </SearchBar>
          <CancelButton
            onClick={() => {
              setSearchBar(null);
            }}
          >
            Cancelar
          </CancelButton>
          <SearchButton onClick={() => handleSearch()}>Buscar</SearchButton>
        </SearchContainer>
      </Menu>
    </Container>
  );
};

export default SystemLogsPage;
